import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const Hero = ({ onExplore }) => {
  const [loaded, setLoaded] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    setLoaded(true);

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative h-screen w-full overflow-hidden">
      {/* Background Image with Parallax */}
      <div
        className="absolute inset-0 w-full h-[120%]"
        style={{
          transform: `translateY(${scrollY * 0.3}px)`,
        }}
      >
        <img
          src="/pics/1.jpeg"
          alt={t('hero.galleryLabel')}
          sizes="100vw"
          fetchpriority="high"
          decoding="async"
          className={`w-full h-full object-cover transition-transform duration-[2000ms] ${
            loaded ? 'scale-100' : 'scale-110'
          }`}
        />
      </div>

      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#2A2520]/70 via-[#2A2520]/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#2A2520]/50 via-transparent to-[#2A2520]/20" />

      {/* Decorative Elements */}
      <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-[#B8963E]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-[#B8963E]/5 rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative h-full flex items-center">
        <div className="max-w-7xl mx-auto px-4 xs:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            {/* Subtitle */}
            <div
              className={`mb-4 xs:mb-6 transition-all duration-1000 delay-300 ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <span className="inline-block px-3 xs:px-4 py-1.5 bg-[#B8963E]/20 backdrop-blur-sm text-[#B8963E] text-xs xs:text-sm tracking-widest uppercase">
                {t('hero.artistLabel')}
              </span>
            </div>

            {/* Title - responsive hero text */}
            <h1
              className={`text-responsive-hero text-white mb-4 xs:mb-6 leading-tight transition-all duration-1000 delay-500 ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {t('hero.firstName')}
              <br />
              <span className="text-gradient-gold">{t('hero.lastName')}</span>
            </h1>

            {/* Description */}
            <p
              className={`text-base xs:text-lg md:text-xl text-[#E8E2D9] mb-6 xs:mb-10 leading-relaxed max-w-lg transition-all duration-1000 delay-700 ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {t('hero.description')}
            </p>

            {/* CTA Buttons - stack on very small screens */}
            <div
              className={`flex flex-col xs:flex-row flex-wrap gap-3 xs:gap-4 transition-all duration-1000 delay-900 ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <button
                onClick={onExplore}
                className="btn-gold group flex items-center justify-center xs:justify-start"
              >
                <span>{t('hero.viewWorks')}</span>
                <svg
                  className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </button>
              <button className="px-6 xs:px-8 py-3 xs:py-3.5 border border-white/30 text-white hover:bg-white/10 transition-all duration-500 tracking-wider text-sm uppercase font-light text-center" style={{ minHeight: '44px' }}>
                {t('hero.aboutArtist')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center transition-all duration-1000 delay-[1200ms] ${
          loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <span className="text-white/60 text-xs tracking-widest uppercase mb-2">
          {t('hero.scroll')}
        </span>
        <div className="w-6 h-10 border border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-white/60 rounded-full animate-bounce" />
        </div>
      </div>

      {/* Side Text */}
      <div className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-px h-20 bg-white/20" />
          <span
            className="text-white/40 text-xs tracking-widest uppercase"
            style={{ writingMode: 'vertical-rl' }}
          >
            {t('hero.galleryLabel')}
          </span>
          <div className="w-px h-20 bg-white/20" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
